import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Users, Settings, Plus, Search, Edit, Trash2, Eye, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';

const Administracion: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/administracion/usuarios" replace />} />
      <Route path="/usuarios" element={<GestionUsuarios />} />
      <Route path="/roles" element={<RolesPermisos />} />
      <Route path="/parametros" element={<ParametrosAdministrativos />} />
    </Routes>
  );
};

const GestionUsuarios: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Datos de prueba
  const usuarios = [
    {
      id: '1',
      username: 'jdoe',
      firstName: 'Juan',
      lastName: 'Doe',
      document: 'CC 12345678',
      email: 'jdoe@civitransito.gov.co',
      role: 'ADMINISTRADOR',
      department: 'Recursos Humanos',
      status: 'ACTIVE',
      sessions: 1,
      lastLogin: '2024-01-15T10:30:00Z'
    },
    {
      id: '2',
      username: 'mariaf',
      firstName: 'María',
      lastName: 'Fernández',
      document: 'CC 87654321',
      email: 'mfernandez@civitransito.gov.co',
      role: 'FUNCIONARIO',
      department: 'Trámites',
      status: 'ACTIVE',
      sessions: 0,
      lastLogin: '2024-01-14T15:45:00Z'
    },
    {
      id: '3',
      username: 'carlosr',
      firstName: 'Carlos',
      lastName: 'Rodríguez',
      document: 'CC 11223344',
      email: 'crodriguez@civitransito.gov.co',
      role: 'CONSULTOR',
      department: 'Consultas',
      status: 'INACTIVE',
      sessions: 0,
      lastLogin: '2024-01-10T09:20:00Z'
    }
  ];

  const filteredUsuarios = usuarios.filter(usuario =>
    usuario.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    usuario.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    usuario.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    usuario.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadge = (role: string) => {
    const colors = {
      'ADMINISTRADOR': 'bg-red-100 text-red-800',
      'FUNCIONARIO': 'bg-blue-100 text-blue-800',
      'CONSULTOR': 'bg-green-100 text-green-800',
      'AUDITOR': 'bg-purple-100 text-purple-800'
    };
    return colors[role as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadge = (status: string) => {
    return status === 'ACTIVE' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  const handleCreateUser = () => {
    setShowCreateModal(true);
    toast.success('Modal de creación de usuario abierto');
  };

  const handleExport = () => {
    toast.success('Exportando usuarios...');
  };

  const handleEdit = (id: string) => {
    toast.success(`Editando usuario ${id}`);
  };

  const handleView = (id: string) => {
    toast.success(`Viendo detalles del usuario ${id}`);
  };

  const handleDelete = (id: string) => {
    toast.success(`Eliminando usuario ${id}`);
  };

  const handleResetPassword = (id: string) => {
    toast.success(`Reseteando password del usuario ${id}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Usuarios</h1>
          <p className="text-gray-600">Administra usuarios, roles y permisos del sistema</p>
        </div>
        <div className="flex space-x-3">
          <button onClick={handleExport} className="btn btn-success">
            <Users className="w-4 h-4 mr-2" />
            Exportar
          </button>
          <button onClick={handleCreateUser} className="btn btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Crear nuevo usuario
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center space-x-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar usuario..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">Usuario</th>
                <th className="table-header-cell">Nombre</th>
                <th className="table-header-cell">Documento</th>
                <th className="table-header-cell">Correo</th>
                <th className="table-header-cell">Rol</th>
                <th className="table-header-cell">Dependencia</th>
                <th className="table-header-cell">Estado</th>
                <th className="table-header-cell">Sesiones</th>
                <th className="table-header-cell">Acciones</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {filteredUsuarios.map((usuario) => (
                <tr key={usuario.id} className="table-row">
                  <td className="table-cell font-medium">{usuario.username}</td>
                  <td className="table-cell">{usuario.firstName} {usuario.lastName}</td>
                  <td className="table-cell">{usuario.document}</td>
                  <td className="table-cell">{usuario.email}</td>
                  <td className="table-cell">
                    <span className={`badge ${getRoleBadge(usuario.role)}`}>
                      {usuario.role}
                    </span>
                  </td>
                  <td className="table-cell">{usuario.department}</td>
                  <td className="table-cell">
                    <span className={`badge ${getStatusBadge(usuario.status)}`}>
                      {usuario.status === 'ACTIVE' ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center space-x-1">
                      <span>{usuario.sessions}</span>
                      <Users className="w-4 h-4 text-gray-400" />
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(usuario.id)}
                        className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleView(usuario.id)}
                        className="p-1 text-green-600 hover:bg-green-100 rounded"
                        title="Ver"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(usuario.id)}
                        className="p-1 text-red-600 hover:bg-red-100 rounded"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleResetPassword(usuario.id)}
                        className="p-1 text-yellow-600 hover:bg-yellow-100 rounded"
                        title="Resetear Password"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const RolesPermisos: React.FC = () => {
  const roles = [
    {
      id: '1',
      name: 'ADMINISTRADOR',
      permissions: ['Usuarios', 'Trámites', 'Reportes', 'Auditoría', 'Parámetros'],
      description: 'Acceso completo al sistema'
    },
    {
      id: '2',
      name: 'FUNCIONARIO',
      permissions: ['Trámites', 'Reportes'],
      description: 'Acceso a módulos operativos'
    },
    {
      id: '3',
      name: 'CONSULTOR',
      permissions: ['Reportes', 'Consultas'],
      description: 'Solo lectura y consultas'
    }
  ];

  const handleCreateRole = () => {
    toast.success('Creando nuevo rol...');
  };

  const handleEdit = (id: string) => {
    toast.success(`Editando rol ${id}`);
  };

  const handleDelete = (id: string) => {
    toast.success(`Eliminando rol ${id}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Roles y Permisos</h1>
          <p className="text-gray-600">Gestiona roles y permisos del sistema</p>
        </div>
        <button onClick={handleCreateRole} className="btn btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          Crear nuevo rol
        </button>
      </div>

      {/* Roles Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">Rol</th>
                <th className="table-header-cell">Permisos</th>
                <th className="table-header-cell">Acciones</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {roles.map((role) => (
                <tr key={role.id} className="table-row">
                  <td className="table-cell">
                    <div>
                      <div className="font-medium text-gray-900">{role.name}</div>
                      <div className="text-sm text-gray-500">{role.description}</div>
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="flex flex-wrap gap-1">
                      {role.permissions.map((permission, index) => (
                        <span key={index} className="badge badge-info text-xs">
                          {permission}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(role.id)}
                        className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(role.id)}
                        className="p-1 text-red-600 hover:bg-red-100 rounded"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ParametrosAdministrativos: React.FC = () => {
  const handleCreateParameter = () => {
    toast.success('Creando nuevo parámetro...');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Parámetros Administrativos</h1>
          <p className="text-gray-600">Configuración general del sistema</p>
        </div>
        <button onClick={handleCreateParameter} className="btn btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo parámetro
        </button>
      </div>

      {/* Parameters Content */}
      <div className="card p-6">
        <div className="text-center py-12">
          <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Parámetros del Sistema</h3>
          <p className="text-gray-600">Configuración de parámetros administrativos</p>
        </div>
      </div>
    </div>
  );
};

export default Administracion;
